import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useViewSearch } from "@/lib/view-search";
import { RepeatingEntriesPage } from "@/features/repeating-entries/RepeatingEntriesPage";
import { journalSearch, journalStatus } from "./journal-search";
import {
  type FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import { useAccountLabels } from "@/lib/labels";
import {
  ApiRequestError,
  type CreateJournalEntryInput,
  type CreateJournalEntryLineInput,
  createJournalEntry,
  type JournalEntry,
  listJournalEntries,
} from "@/lib/api";

interface EntryLineDraft {
  uid: string;
  account_id: string;
  debit: string;
  credit: string;
}

function emptyEntryLine(): EntryLineDraft {
  return { uid: crypto.randomUUID(), account_id: "", debit: "", credit: "" };
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return 0;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function toErrorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError && cause.status === 409) {
    return (
      "This entry falls in a locked accounting period (409 locked_period) — " +
      "the period lock rejects it. Ask an admin to review the lock in " +
      "Settings → Books → Period locks, or use a later entry date. " +
      `Server detail: ${cause.message}`
    );
  }
  return cause instanceof Error ? cause.message : String(cause);
}

function yearStartInput(now: Date): string {
  return `${now.getFullYear()}-01-01`;
}

function todayInput(now: Date): string {
  return now.toISOString().slice(0, 10);
}

export function JournalPage() {
  const navigate = useNavigate();
  const search = useRouterState({
    select: (state) => state.location.search,
  }) as Record<string, unknown>;
  const urlAccountId =
    typeof search.account_id === "string" ? search.account_id : "";
  const urlStart =
    typeof search.start_date === "string" ? search.start_date : "";
  const urlEnd = typeof search.end_date === "string" ? search.end_date : "";
  const status = journalStatus(search.status);
  const [view, setView] = useViewSearch(
    "view",
    ["entries", "repeating"] as const,
    "entries",
  );

  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(
    () => urlStart || yearStartInput(new Date()),
  );
  const [endDate, setEndDate] = useState(
    () => urlEnd || todayInput(new Date()),
  );
  const [accountId, setAccountId] = useState(() => urlAccountId);
  const [applied, setApplied] = useState<{
    start_date: string;
    end_date: string;
    account_id?: string;
  }>(() => ({
    start_date: urlStart || yearStartInput(new Date()),
    end_date: urlEnd || todayInput(new Date()),
    account_id: urlAccountId || undefined,
  }));

  // The unsaved entry lives here, on the page — not in the generic documentDraft
  // mirror (a balanced debit/credit journal has no single amount or party
  // field). Closing the drawer leaves it untouched so a half-typed entry can
  // be resumed; only successful draft creation or Discard clears it.
  const [entryDate, setEntryDate] = useState(() => todayInput(new Date()));
  const [memo, setMemo] = useState("");
  const [entryLines, setEntryLines] = useState<EntryLineDraft[]>([
    emptyEntryLine(),
    emptyEntryLine(),
  ]);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [pendingPost, setPendingPost] = useState<CreateJournalEntryInput | null>(
    null,
  );
  const [posting, setPosting] = useState(false);
  const [createdDraft, setCreatedDraft] = useState<JournalEntry | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const accountLabels = useAccountLabels();

  const run = useCallback(
    (range: { start_date: string; end_date: string; account_id?: string }) => {
      setLoading(true);
      setError(null);
      listJournalEntries(range)
        .then(setEntries)
        .catch((cause: unknown) => setError(toErrorMessage(cause)))
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  // Statement drill-through lands here with `account_id`/`start_date`/
  // `end_date` in the URL. Seed the form from it and re-apply on change
  // without clobbering a range the user typed in this session.
  useEffect(() => {
    if (!urlStart && !urlEnd && !urlAccountId) return;
    const next = {
      start_date: urlStart || yearStartInput(new Date()),
      end_date: urlEnd || todayInput(new Date()),
      account_id: urlAccountId || undefined,
    };
    setStartDate(next.start_date);
    setEndDate(next.end_date);
    setAccountId(urlAccountId);
    setApplied((prev) =>
      prev.start_date === next.start_date &&
      prev.end_date === next.end_date &&
      (prev.account_id ?? "") === (next.account_id ?? "")
        ? prev
        : next,
    );
  }, [urlStart, urlEnd, urlAccountId]);

  const submitRange = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      setError("Start and end dates are both required.");
      return;
    }
    if (endDate < startDate) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({
      start_date: startDate,
      end_date: endDate,
      account_id: accountId.trim() || undefined,
    });
    void navigate({ to: "/journal", search: previous => journalSearch(previous, { start_date: startDate, end_date: endDate, account_id: accountId.trim(), status }) });
  };

  const updateEntryLine = (index: number, patch: Partial<EntryLineDraft>) => {
    setEntryLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );
  };

  const removeEntryLine = (index: number) => {
    setEntryLines((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  const debitPreview = entryLines.reduce(
    (sum, line) => sum + (toMinor(line.debit) ?? 0),
    0,
  );
  const creditPreview = entryLines.reduce(
    (sum, line) => sum + (toMinor(line.credit) ?? 0),
    0,
  );
  const outOfBalance = debitPreview !== creditPreview;

  const hasDraft =
    memo.trim() !== "" ||
    entryLines.some((line) => line.account_id || line.debit || line.credit);

  const clearEntryDraft = () => {
    setMemo("");
    setEntryLines([emptyEntryLine(), emptyEntryLine()]);
    setPostError(null);
  };

  const openCreator = () => {
    setPostError(null);
    setCreatorOpen(true);
  };

  // Validate on submit, then stage the payload for the confirmation dialog.
  // Nothing reaches the API until the user confirms in the Dialog.
  const stageEntry = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPostError(null);
    try {
      if (!entryDate) throw new Error("Entry date is required.");
      if (!memo.trim()) throw new Error("Memo is required.");
      if (entryLines.length === 0) throw new Error("Add at least one line.");
      const payloadLines: CreateJournalEntryLineInput[] = entryLines.map(
        (line, i) => {
          if (!line.account_id.trim())
            throw new Error(`Line ${i + 1}: choose an account.`);
          const debit_cents = toMinor(line.debit);
          const credit_cents = toMinor(line.credit);
          if (debit_cents === null || credit_cents === null)
            throw new Error(
              `Line ${i + 1}: debit/credit must be non-negative numbers.`,
            );
          if (debit_cents === 0 && credit_cents === 0)
            throw new Error(`Line ${i + 1}: enter a debit or a credit amount.`);
          if (debit_cents > 0 && credit_cents > 0)
            throw new Error(
              `Line ${i + 1}: a line is either a debit or a credit, not both.`,
            );
          return {
            account_id: line.account_id.trim(),
            debit_cents,
            credit_cents,
          };
        },
      );
      const debitTotal = payloadLines.reduce((s, l) => s + l.debit_cents, 0);
      const creditTotal = payloadLines.reduce((s, l) => s + l.credit_cents, 0);
      if (debitTotal !== creditTotal)
        throw new Error(
          `Entry is out of balance: debits ${formatMoney({ cents: debitTotal, currency: "USD" })} ≠ credits ${formatMoney({ cents: creditTotal, currency: "USD" })}.`,
        );
      setPendingPost({
        entry_date: entryDate,
        memo: memo.trim(),
        lines: payloadLines,
      });
    } catch (cause) {
      setPostError(toErrorMessage(cause));
    }
  };

  const confirmPost = async () => {
    if (!pendingPost) return;
    setPosting(true);
    setPostError(null);
    try {
      const created = await createJournalEntry(pendingPost);
      if (!created.id || created.status !== "draft") {
        throw new Error("Draft creation was not confirmed. Check the ledger before retrying.");
      }
      setCreatedDraft(created);
      setPendingPost(null);
      setMemo("");
      setEntryLines([emptyEntryLine(), emptyEntryLine()]);
      setCreatorOpen(false);
      run(applied);
    } catch (cause) {
      setPendingPost(null);
      setPostError(toErrorMessage(cause));
    } finally {
      setPosting(false);
    }
  };

  // Cancelling returns to the still-open drawer with the unsaved entry intact.
  const cancelPost = () => {
    if (!posting) setPendingPost(null);
  };

  const pendingDebitTotal =
    pendingPost?.lines.reduce((sum, line) => sum + line.debit_cents, 0) ?? 0;

  const visibleEntries = (entries ?? []).filter(entry => status === "all" || entry.status === status);
  const rowEmptyMessage = status !== "all"
    ? `No ${status === "void" ? "voided" : status} journal entries in this query — try All entries or adjust the dates/account.`
    : applied.account_id
    ? "No journal entries for the selected account in this range — clear the account filter or widen the dates."
    : "No journal entries in this range — create a draft or adjust the dates.";

  const entryDateText = (entry: JournalEntry) =>
    typeof entry.entry_date === "string"
      ? entry.entry_date.slice(0, 10)
      : String(entry.entry_date);

  return (
    <div className="grid gap-6">
      <PageHeader
        title="General ledger"
        description="Review journal entries or repeating templates. Automated postings (invoices, depreciation, loan payments) appear here too."
        actions={
          view === "entries" ? (
            <Button type="button" onClick={openCreator}>
              New journal entry
            </Button>
          ) : undefined
        }
      />

      <Tabs
        label="General ledger view"
        tabs={[
          { value: "entries", label: "Journal entries" },
          { value: "repeating", label: "Repeating entries" },
        ]}
        value={view}
        onValueChange={(value) =>
          setView(value === "repeating" ? "repeating" : "entries")
        }
      >
        {view === "repeating" ? <RepeatingEntriesPage embedded /> : null}
      </Tabs>

      {view === "entries" ? (
        <>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {error.includes("No Console organization context") ? (
            <p className="mt-1 text-[var(--muted-foreground)]">
              Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
            </p>
          ) : null}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => run(applied)}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={submitRange}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="je-start" className="grid gap-1 text-xs font-medium">
          Start date
          <Input
            id="je-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="je-end" className="grid gap-1 text-xs font-medium">
          End date
          <Input
            id="je-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <div className="grid gap-1 text-xs font-medium">
          <span>Account (optional)</span>
          <div className="flex items-center gap-2">
            <AccountPicker
              id="je-account"
              value={accountId}
              onChange={setAccountId}
              disabled={loading}
              ariaLabel="Filter by account"
            />
            {accountId ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAccountId("")}
                disabled={loading}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
        <Button type="submit" disabled={loading}>
          {loading ? "Loading..." : "Run query"}
        </Button>
      </form>

      <Tabs label="Journal status" tabs={[{ value: "all", label: "All entries" }, { value: "draft", label: "Drafts" }, { value: "posted", label: "Posted" }, { value: "void", label: "Voided" }]} value={status} onValueChange={value => { void navigate({ to: "/journal", search: previous => journalSearch(previous, { ...applied, status: value }) }); }}>
      <p className="mb-3 text-xs text-[var(--muted-foreground)]">Status filters apply to the entries returned for this date and account query. Draft journals are not posted to the ledger.</p>
      {error && entries === null ? null : (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="journal-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Memo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead>Lines</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && entries === null ? (
                    <TableRow>
                      <TableCell colSpan={7}>Loading…</TableCell>
                    </TableRow>
                  ) : visibleEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>{rowEmptyMessage}</TableCell>
                    </TableRow>
                  ) : (
                    visibleEntries.map((entry) => (
                      <Fragment key={entry.id}>
                        <TableRow>
                          <TableCell className="font-mono text-xs">
                            <Link
                              to="/journal/$entryId"
                              params={{ entryId: entry.id }}
                              search={previous => journalSearch(previous, { ...applied, status })}
                              className="hover:underline"
                            >
                              {entry.entry_number}
                            </Link>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {entryDateText(entry)}
                          </TableCell>
                          <TableCell>{entry.memo}</TableCell>
                          <TableCell>
                            <Badge
                              tone={
                                entry.status === "posted"
                                  ? "success"
                                  : entry.status === "void"
                                    ? "danger"
                                    : "neutral"
                              }
                            >
                              {entry.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: entry.total_debit_cents,
                              currency: "USD",
                            })}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: entry.total_credit_cents,
                              currency: "USD",
                            })}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setExpanded((prev) => ({
                                  ...prev,
                                  [entry.id]: !prev[entry.id],
                                }))
                              }
                              aria-expanded={Boolean(expanded[entry.id])}
                            >
                              {expanded[entry.id]
                                ? "Hide"
                                : `Show (${entry.lines.length})`}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expanded[entry.id]
                          ? entry.lines.map((line, i) => (
                              <TableRow key={`${entry.id}-line-${i}`}>
                                <TableCell />
                                <TableCell className="text-xs">
                                  {accountLabels.has(line.account_id) ? (
                                    accountLabels.get(line.account_id)
                                  ) : (
                                    <span
                                      className="text-[var(--muted-foreground)]"
                                      title={line.account_id}
                                    >
                                      Unknown account
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell className="text-right tabular-nums">
                                  {line.debit_cents > 0 ? (
                                    formatMoney({
                                      cents: line.debit_cents,
                                      currency: "USD",
                                    })
                                  ) : (
                                    <span className="text-[var(--muted-foreground)]">
                                      —
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {line.credit_cents > 0 ? (
                                    formatMoney({
                                      cents: line.credit_cents,
                                      currency: "USD",
                                    })
                                  ) : (
                                    <span className="text-[var(--muted-foreground)]">
                                      —
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            ))
                          : null}
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="journal-stack"
          >
            {loading && entries === null ? (
              <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
            ) : visibleEntries.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                {rowEmptyMessage}
              </p>
            ) : (
              visibleEntries.map((entry) => (
                <article
                  key={entry.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <Link
                      to="/journal/$entryId"
                      params={{ entryId: entry.id }}
                      search={previous => journalSearch(previous, { ...applied, status })}
                      className="record-title underline-offset-4 hover:underline"
                    >
                      {entry.entry_number}
                    </Link>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: entry.total_debit_cents,
                        currency: "USD",
                      })}
                    </span>
                  </div>
                  <CardField label="Date">{entryDateText(entry)}</CardField>
                  <CardField label="Memo">{entry.memo}</CardField>
                  <CardField label="Status">
                    <Badge
                      tone={
                        entry.status === "posted"
                          ? "success"
                          : entry.status === "void"
                            ? "danger"
                            : "neutral"
                      }
                      className="justify-self-start"
                    >
                      {entry.status}
                    </Badge>
                  </CardField>
                  <div className="flex flex-wrap gap-2 pt-3">
                    <Button asChild variant="secondary" size="sm">
                      <Link
                        to="/journal/$entryId"
                        params={{ entryId: entry.id }}
                        search={previous => journalSearch(previous, { ...applied, status })}
                      >
                        Open
                      </Link>
                    </Button>
                  </div>
                </article>
              ))
            )}
          </div>
        </>
      )}
      </Tabs>

      {createdDraft ? (
        <section role="status" className="surface rounded-xl p-4 text-sm">
          <p>Draft created — this entry is not posted to the ledger.</p>
          <Link to="/journal/$entryId" params={{ entryId: createdDraft.id }} search={previous => journalSearch(previous)} className="text-[var(--primary)] underline">View draft</Link>
        </section>
      ) : null}

      <Drawer
        open={creatorOpen && pendingPost === null}
        onOpenChange={setCreatorOpen}
        title="New journal entry"
        description="Create a balanced draft for review. This does not post to the ledger. Closing this panel keeps your unsaved entry on this page."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreatorOpen(false)}
              disabled={posting}
            >
              Back to ledger
            </Button>
            {hasDraft ? (
              <Button
                type="button"
                variant="ghost"
                onClick={clearEntryDraft}
                disabled={posting}
              >
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="journal-entry-form"
              disabled={posting || outOfBalance}
              className="ms-auto"
              title={
                outOfBalance
                  ? "Debits must equal credits before creating a draft"
                  : undefined
              }
            >
              {posting ? "Creating…" : "Create draft"}
            </Button>
          </>
        }
      >
        <form
          id="journal-entry-form"
          onSubmit={stageEntry}
          className="grid gap-5"
          aria-label="Create manual journal draft"
        >
          {postError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {postError}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label
              htmlFor="je-date"
              className="grid gap-1 text-xs font-medium"
            >
              Entry date
              <Input
                id="je-date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                required
                disabled={posting}
              />
            </label>
            <label
              htmlFor="je-memo"
              className="grid gap-1 text-xs font-medium"
            >
              Memo
              <Input
                id="je-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                required
                placeholder="Year-end adjustment"
                disabled={posting}
              />
            </label>
          </div>

          <fieldset className="grid gap-3">
            <legend className="text-xs font-medium">Lines</legend>
            {entryLines.map((line, index) => (
              <div
                key={line.uid}
                className="surface grid gap-2 rounded-xl border border-[var(--border)] p-3"
              >
                <div className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
                  <label
                    htmlFor={`je-line-${index}-account`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Account
                    <AccountPicker
                      id={`je-line-${index}-account`}
                      value={line.account_id}
                      onChange={(value) =>
                        updateEntryLine(index, { account_id: value })
                      }
                      required
                      disabled={posting}
                    />
                  </label>
                  <label
                    htmlFor={`je-line-${index}-debit`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Debit (USD)
                    <Input
                      id={`je-line-${index}-debit`}
                      type="number"
                      value={line.debit}
                      onChange={(e) =>
                        updateEntryLine(index, { debit: e.target.value })
                      }
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={posting}
                    />
                  </label>
                  <label
                    htmlFor={`je-line-${index}-credit`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Credit (USD)
                    <Input
                      id={`je-line-${index}-credit`}
                      type="number"
                      value={line.credit}
                      onChange={(e) =>
                        updateEntryLine(index, { credit: e.target.value })
                      }
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={posting}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEntryLine(index)}
                    disabled={entryLines.length <= 1 || posting}
                    aria-label={`Remove line ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setEntryLines((prev) => [...prev, emptyEntryLine()])
                }
                disabled={posting}
              >
                Add line
              </Button>
            </div>
          </fieldset>

          <p className="text-sm" aria-live="polite">
            Debits{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: debitPreview, currency: "USD" })}
            </span>{" "}
            · Credits{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: creditPreview, currency: "USD" })}
            </span>
            {outOfBalance ? (
              <span className="text-[var(--danger)]">
                {" "}
                — out of balance: debits must equal credits before creating a draft.
              </span>
            ) : (
              <span className="text-[var(--muted-foreground)]">
                {" "}
                — balanced.
              </span>
            )}
          </p>
        </form>
      </Drawer>

      <Dialog
        open={pendingPost !== null}
        onOpenChange={(open) => {
          if (!open) cancelPost();
        }}
        title="Create this journal draft?"
        description={
          pendingPost
            ? `Creates a balanced draft with ${formatMoney({ cents: pendingDebitTotal, currency: "USD" })} of debits and credits dated ${pendingPost.entry_date}. This does not post to the ledger or change posted balances.`
            : undefined
        }
      >
        {pendingPost ? (
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={cancelPost}
              disabled={posting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={confirmPost} disabled={posting}>
              {posting ? "Creating…" : "Create draft"}
            </Button>
          </div>
        ) : null}
      </Dialog>
        </>
      ) : null}
    </div>
  );
}
